import { useRequiredContext } from "@redotech/react-util/context";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { StorefrontClientContext } from "@redotech/redo-merchant-app-common/team";
import {
  UpsellConditionSource,
  UpsellConditionStep,
} from "@redotech/redo-model/advanced-flow/steps";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  ProductSelectionModal,
  storefrontProductToModalProduct,
} from "@redotech/redo-web/product/product-selection-modal";
import { searchProducts } from "@redotech/shopify-storefront";
import { useState } from "react";
import { FrontendStepType, PartialWithRequiredType } from ".";
import { CollectionSelect } from "../../../../conversion/post-purchase/builder/post-purchase-elements/collection-select";

const textValues: Record<UpsellConditionSource, string> = {
  product: "Has specific product in cart",
  collection: "Has specific collection in cart",
};

const options: RedoListItem<UpsellConditionSource>[] = Object.values(
  UpsellConditionSource,
).map((source) => {
  return { id: source, type: "text", text: textValues[source], value: source };
});

export const upsellConditionStepType: FrontendStepType<
  PartialWithRequiredType<UpsellConditionStep>
> = {
  valid: (step) => {
    const hasProductsDefined =
      !!step.expression?.products && step.expression.products.length > 0;
    const hasCollectionDefined = !!step.expression?.collection;
    return step.isDefault || hasProductsDefined || hasCollectionDefined;
  },
  type: "standard",
  inspectorFactory: (props) => ({
    header: { title: props.step.name ?? "Post purchase upsell" },
    sections: props.step.isDefault
      ? []
      : [
          {
            fields: [
              {
                path: "name",
                description: "Name of the segment you're building",
                widget: "text",
              },
              {
                path: "expression.source",
                label: "Condition",
                widget: "custom",
                Element: (props) => {
                  return (
                    <RedoSingleSelectDropdownInput
                      options={options}
                      optionSelected={({ item }) => {
                        props.valueChange({
                          ...props.value,
                          expression: {
                            ...props.value.expression!,
                            source: item.value,
                          },
                        });
                      }}
                      placeholder="Select an option"
                      selectedItem={options.find(
                        (o) => o.value === props.value.expression?.source,
                      )}
                      size="xs"
                    />
                  );
                },
              },
              {
                path: "expression.product",
                label: "Products",
                visibleIf: (props) =>
                  props.expression?.source === UpsellConditionSource.PRODUCT,
                widget: "custom",
                Element: (props) => {
                  const storefrontClient = useRequiredContext(
                    StorefrontClientContext,
                  );
                  const [modalOpen, setModalOpen] = useState(false);

                  return (
                    <>
                      {props.value.expression?.products &&
                        props.value.expression.products.map((product, i) => {
                          return (
                            <RedoButton
                              hierarchy="secondary"
                              IconTrailing={() => <TrashIcon />}
                              key={i}
                              onClick={() =>
                                props.valueChange({
                                  ...props.value,
                                  expression: {
                                    ...props.value.expression!,
                                    products: (
                                      props.value.expression?.products ?? []
                                    ).filter((p) => p?.id !== product?.id),
                                  },
                                })
                              }
                              size="sm"
                              text={product?.name}
                            />
                          );
                        })}
                      <RedoButton
                        hierarchy="secondary"
                        onClick={() => setModalOpen(true)}
                        size="sm"
                        text="Add product"
                      />
                      <ProductSelectionModal
                        open={modalOpen}
                        searchProducts={async ({ search }) => {
                          const results = await searchProducts(
                            storefrontClient,
                            search,
                            100,
                          );
                          return results
                            ? results.map(storefrontProductToModalProduct)
                            : undefined;
                        }}
                        setOpen={setModalOpen}
                        setSelected={(selected) => {
                          if (selected) {
                            props.valueChange({
                              ...props.value,
                              expression: {
                                ...props.value.expression!,
                                products: [
                                  ...(props.value.expression?.products ?? []),
                                  ...selected.map((s) => ({
                                    id: s.product.id,
                                    name: s.product.title,
                                  })),
                                ],
                              },
                            });
                          }
                        }}
                      />
                    </>
                  );
                },
              },
              {
                path: "expression.collection",
                label: "Collection",
                visibleIf: (props) =>
                  props.expression?.source === UpsellConditionSource.COLLECTION,
                widget: "custom",
                Element: (props) => {
                  return (
                    <CollectionSelect
                      value={props.value.expression?.collection}
                      valueChange={(newVal) =>
                        props.valueChange({
                          ...props.value,
                          expression: {
                            ...props.value.expression!,
                            collection: newVal,
                          },
                        })
                      }
                    />
                  );
                },
              },
            ],
          },
        ],
  }),
};
